/**
 * When a schedule's cron will actually fire — computed by the SAME engine that fires it:
 * `cron-parser`, pinned to the exact version BullMQ uses (`schedule-cron-engine-lock.test.ts`
 * fails if they drift). BullMQ's job scheduler computes each fire as
 * `parseExpression(pattern, { ...repeatOpts, currentDate }).next()`; this is that call, so
 * the times a person reads and approves are the times it runs — every field form, the
 * day-of-month / day-of-week rules, and every DST switch included, by construction.
 *
 * `triggers.json` is hand-editable: anything the validator would not register, or the engine
 * cannot read, returns `null` (the caller shows the raw pattern); never throws.
 *
 * Residual (accepted, Codex 2026-09-30): same ENGINE, not the same zone DATA — the browser's
 * Intl/ICU tz rules evaluate here, Node's on the server. A stale browser can show a different
 * instant around a DST rule its database predates (a zone that changed its DST law recently).
 */
// Default import: cron-parser is CommonJS (`module.exports = CronParser`), and a NAMED import
// of it fails under plain Node ESM — the default import works in every loader.
import cronParser from 'cron-parser'
import { isPlausibleCron, isValidTimeZone } from '@papan/types'

export function nextCronRuns(cron: unknown, tz: unknown, count: number, from: number = Date.now()): number[] | null {
  if (typeof cron !== 'string' || !isPlausibleCron(cron) || !isValidTimeZone(tz) || count < 1) return null
  const out: number[] = []
  try {
    const it = cronParser.parseExpression(cron.trim(), { currentDate: new Date(from), tz })
    while (out.length < count) out.push(it.next().getTime())
  } catch {
    // The engine found no (further) time — e.g. a day that never occurs. What it did find stands.
  }
  return out.length > 0 ? out : null
}

const formatters = new Map<string, Intl.DateTimeFormat>()
function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    })
    formatters.set(tz, f)
  }
  return f
}

/** "Wed 30 Sep, 09:00" in `tz` (with the year when it isn't `now`'s year there). Built from
 *  parts, so it reads the same in every engine / ICU build. */
export function formatZonedTime(at: number, tz: string, now: number = Date.now()): string {
  const parts = (t: number) => {
    const p = formatterFor(tz).formatToParts(new Date(t))
    return (type: string) => p.find((x) => x.type === type)?.value ?? ''
  }
  const v = parts(at)
  const year = v('year') !== parts(now)('year') ? ` ${v('year')}` : ''
  return `${v('weekday')} ${v('day')} ${v('month')}${year}, ${v('hour')}:${v('minute')}`
}
